import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { Skeleton } from '@/components/feedback/skeleton'
import { Input } from '@/components/forms/input'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import defaultLogoUrl from '@/assets/logo.svg'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import {
  defaultChatUserBubbleColor,
  defaultHubAgentName,
  normalizeChatUserBubbleColor,
  normalizeBrandingSettings,
  resolveChatUserBubbleTextColor,
  setPublicBrandingQueryData,
} from '@/features/shared/brand/branding-core'

import {
  deleteBrandAsset,
  getBrandingSettings,
  updateBrandingSettings,
  uploadBrandAsset,
  type AdminSystemSettings,
  type BrandAssetType,
} from './api'
import { WorkspaceBackgroundEditor } from './workspace-background-editor'

type BrandingErrors = Partial<
  Record<'chatUserBubbleColor' | 'hubAgentName' | 'hubAgentWelcomeTitle' | 'title', string>
>

interface BrandingFormState {
  chatUserBubbleColor: string
  description: string
  favicon: string
  hubAgentName: string
  hubAgentWelcomeTitle: string
  logo: string
  title: string
}

const queryKey = ['admin-branding-settings']

function readFormState(settings: AdminSystemSettings): BrandingFormState {
  const config = settings.config

  return {
    chatUserBubbleColor: normalizeChatUserBubbleColor(config.chat_user_bubble_color),
    description: config.description ?? '',
    favicon: config.favicon ?? config.brand_assets?.favicon?.url ?? '',
    hubAgentName: config.hub_agent_name ?? defaultHubAgentName,
    hubAgentWelcomeTitle: config.hub_agent_welcome_title ?? '',
    logo: config.logo ?? config.brand_assets?.logo?.url ?? '',
    title: normalizeBrandingSettings(config).title,
  }
}

function formKey(settings: AdminSystemSettings) {
  const config = settings.config

  return [
    config.chat_user_bubble_color ?? '',
    config.title ?? '',
    config.hub_agent_name ?? '',
    config.hub_agent_welcome_title ?? '',
    config.description ?? '',
  ].join(':')
}

function workspaceBackgroundKey(settings: AdminSystemSettings) {
  const config = settings.config

  return [
    config.workspace_background?.version ?? '',
    config.workspace_background_source?.primary?.key ?? '',
    config.workspace_background_source?.mobile_override?.key ?? '',
  ].join(':')
}

export function AdminBrandingSettingsPage() {
  const { t } = useTranslation('adminSettings')
  const settingsQuery = useQuery({
    queryFn: getBrandingSettings,
    queryKey,
  })

  return (
    <AdminShellPage activePath="/admin/settings/branding">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        {settingsQuery.isError ? (
          <>
            <PageHeader
              contentClassName="max-w-[820px]"
              description={t('branding.description')}
              title={t('branding.title')}
            />
            <div className="flex flex-1 items-center justify-center px-6">
              <ErrorState
                description={t('branding.loadDescription')}
                onRetry={() => {
                  void settingsQuery.refetch()
                }}
                title={t('branding.loadTitle')}
              />
            </div>
          </>
        ) : settingsQuery.isLoading || !settingsQuery.data ? (
          <>
            <PageHeader
              contentClassName="max-w-[820px]"
              description={t('branding.description')}
              title={t('branding.title')}
            />
            <div className="px-5 py-5 lg:px-[30px]">
              <PageContentBoundary className="grid max-w-[820px] gap-5">
                <Skeleton className="h-[210px] rounded-lg" />
                <Skeleton className="h-[310px] rounded-lg" />
                <Skeleton className="h-[170px] rounded-lg" />
              </PageContentBoundary>
            </div>
          </>
        ) : (
          <BrandingSettingsForm key={formKey(settingsQuery.data)} settings={settingsQuery.data} />
        )}
      </div>
    </AdminShellPage>
  )
}

function BrandingSettingsForm({ settings }: { settings: AdminSystemSettings }) {
  const { t } = useTranslation('adminSettings')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const initialState = useMemo(() => readFormState(settings), [settings])
  const [formState, setFormState] = useState(initialState)
  const [savedFormState, setSavedFormState] = useState(initialState)
  const [errors, setErrors] = useState<BrandingErrors>({})
  const [resetAssetType, setResetAssetType] = useState<BrandAssetType | null>(null)
  const isFormDirty =
    formState.chatUserBubbleColor !== savedFormState.chatUserBubbleColor ||
    formState.description !== savedFormState.description ||
    formState.hubAgentName !== savedFormState.hubAgentName ||
    formState.hubAgentWelcomeTitle !== savedFormState.hubAgentWelcomeTitle ||
    formState.title !== savedFormState.title

  const saveMutation = useMutation({
    mutationFn: async () => {
      const nextErrors: BrandingErrors = {}
      if (!formState.title.trim()) {
        nextErrors.title = t('branding.validation.titleRequired')
      }
      if (!/^#[0-9a-fA-F]{6}$/.test(formState.chatUserBubbleColor.trim())) {
        nextErrors.chatUserBubbleColor = t('branding.validation.chatUserBubbleColorInvalid')
      }
      if (Array.from(formState.hubAgentName.trim()).length > 20) {
        nextErrors.hubAgentName = t('branding.validation.hubAgentNameTooLong')
      }
      if (Array.from(formState.hubAgentWelcomeTitle.trim()).length > 60) {
        nextErrors.hubAgentWelcomeTitle = t('branding.validation.hubAgentWelcomeTitleTooLong')
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        window.requestAnimationFrame(() => {
          if (nextErrors.title) {
            document.getElementById('branding-title')?.focus()
          } else if (nextErrors.chatUserBubbleColor) {
            document.getElementById('branding-chat-user-bubble-color')?.focus()
          } else if (nextErrors.hubAgentName) {
            document.getElementById('branding-hub-agent-name')?.focus()
          } else if (nextErrors.hubAgentWelcomeTitle) {
            document.getElementById('branding-hub-agent-welcome-title')?.focus()
          }
        })
        return undefined
      }

      setErrors({})
      return updateBrandingSettings({
        chatUserBubbleColor: formState.chatUserBubbleColor,
        description: formState.description,
        hubAgentName: formState.hubAgentName,
        hubAgentWelcomeTitle: formState.hubAgentWelcomeTitle,
        title: formState.title,
      })
    },
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (result) => {
      if (!result) {
        return
      }

      const nextFormState = readFormState(result)
      setFormState(nextFormState)
      setSavedFormState(nextFormState)
      setPublicBrandingQueryData(queryClient, result.config)
      await queryClient.invalidateQueries({ queryKey })
      showFeedback({
        message: t('success.updateBrandingSettings'),
        tone: 'success',
      })
    },
  })

  const uploadMutation = useMutation({
    mutationFn: uploadBrandAsset,
    onError: () => {
      showFeedback({
        message: t('branding.assetUploadFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (result, variables) => {
      setFormState((current) => ({
        ...current,
        [variables.assetType]:
          result.config[variables.assetType] ??
          result.config.brand_assets?.[variables.assetType]?.url ??
          '',
      }))
      setPublicBrandingQueryData(queryClient, result.config)
      await queryClient.invalidateQueries({ queryKey })
      showFeedback({
        message: t(`branding.assetUploaded.${variables.assetType}`),
        tone: 'success',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteBrandAsset,
    onError: () => {
      showFeedback({
        message: t('branding.assetDeleteFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (result, assetType) => {
      setFormState((current) => ({ ...current, [assetType]: '' }))
      setResetAssetType(null)
      setPublicBrandingQueryData(queryClient, result.config)
      await queryClient.invalidateQueries({ queryKey })
      showFeedback({
        message: t(`branding.assetDeleted.${assetType}`),
        tone: 'success',
      })
    },
  })
  const assetControlsLocked = uploadMutation.isPending || deleteMutation.isPending

  const updateValue = (field: keyof BrandingFormState, value: string) => {
    setFormState((current) => ({ ...current, [field]: value }))
    if (
      field === 'title' ||
      field === 'chatUserBubbleColor' ||
      field === 'hubAgentName' ||
      field === 'hubAgentWelcomeTitle'
    ) {
      setErrors((current) => ({ ...current, [field]: undefined }))
    }
  }

  const closeResetDialog = () => {
    if (!deleteMutation.isPending) {
      setResetAssetType(null)
    }
  }

  const confirmResetAsset = () => {
    if (resetAssetType) {
      deleteMutation.mutate(resetAssetType)
    }
  }

  const resetAssetLabel = resetAssetType ? t(`branding.assetNames.${resetAssetType}`) : ''

  return (
    <>
      <PageHeader
        contentClassName="max-w-[820px]"
        description={t('branding.description')}
        title={t('branding.title')}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-[30px]">
        <PageContentBoundary className="grid max-w-[820px] gap-5">
          <SettingsCard
            description={t('branding.basicDescription')}
            title={t('branding.basicTitle')}
          >
            <div className="grid gap-4">
              <FieldBlock
                error={errors.title}
                htmlFor="branding-title"
                label={t('branding.fields.title')}
                required
              >
                <Input
                  aria-describedby={errors.title ? 'branding-title-error' : undefined}
                  id="branding-title"
                  invalid={Boolean(errors.title)}
                  onChange={(event) => updateValue('title', event.target.value)}
                  placeholder={t('branding.placeholders.title')}
                  value={formState.title}
                />
              </FieldBlock>
              <FieldBlock htmlFor="branding-description" label={t('branding.fields.description')}>
                <Input
                  id="branding-description"
                  onChange={(event) => updateValue('description', event.target.value)}
                  placeholder={t('branding.placeholders.description')}
                  value={formState.description}
                />
              </FieldBlock>
            </div>
          </SettingsCard>

          <SettingsCard
            description={t('branding.conversationDescription')}
            title={t('branding.conversationTitle')}
          >
            <div className="grid gap-4">
              <FieldBlock
                error={errors.hubAgentName}
                htmlFor="branding-hub-agent-name"
                hint={t('branding.hints.hubAgentName')}
                label={t('branding.fields.hubAgentName')}
              >
                <Input
                  aria-describedby={
                    errors.hubAgentName
                      ? 'branding-hub-agent-name-error'
                      : 'branding-hub-agent-name-hint'
                  }
                  id="branding-hub-agent-name"
                  invalid={Boolean(errors.hubAgentName)}
                  onChange={(event) => updateValue('hubAgentName', event.target.value)}
                  placeholder={t('branding.placeholders.hubAgentName')}
                  value={formState.hubAgentName}
                />
              </FieldBlock>
              <FieldBlock
                error={errors.hubAgentWelcomeTitle}
                htmlFor="branding-hub-agent-welcome-title"
                hint={t('branding.hints.hubAgentWelcomeTitle')}
                label={t('branding.fields.hubAgentWelcomeTitle')}
              >
                <Input
                  aria-describedby={
                    errors.hubAgentWelcomeTitle
                      ? 'branding-hub-agent-welcome-title-error'
                      : 'branding-hub-agent-welcome-title-hint'
                  }
                  id="branding-hub-agent-welcome-title"
                  invalid={Boolean(errors.hubAgentWelcomeTitle)}
                  onChange={(event) => updateValue('hubAgentWelcomeTitle', event.target.value)}
                  placeholder={t('branding.placeholders.hubAgentWelcomeTitle')}
                  value={formState.hubAgentWelcomeTitle}
                />
              </FieldBlock>
              <FieldBlock
                error={errors.chatUserBubbleColor}
                htmlFor="branding-chat-user-bubble-color"
                hint={t('branding.hints.chatUserBubbleColor')}
                label={t('branding.fields.chatUserBubbleColor')}
              >
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px] sm:items-center">
                  <div className="flex gap-2">
                    <input
                      aria-label={t('branding.fields.chatUserBubbleColorPicker')}
                      className="h-[38px] w-12 shrink-0 cursor-pointer rounded-lg border border-[var(--color-control-border)] bg-white p-1"
                      onChange={(event) =>
                        updateValue('chatUserBubbleColor', event.target.value.toUpperCase())
                      }
                      type="color"
                      value={normalizeChatUserBubbleColor(formState.chatUserBubbleColor)}
                    />
                    <Input
                      aria-describedby={
                        errors.chatUserBubbleColor
                          ? 'branding-chat-user-bubble-color-error'
                          : 'branding-chat-user-bubble-color-hint'
                      }
                      id="branding-chat-user-bubble-color"
                      invalid={Boolean(errors.chatUserBubbleColor)}
                      maxLength={7}
                      onChange={(event) =>
                        updateValue('chatUserBubbleColor', event.target.value.toUpperCase())
                      }
                      placeholder={defaultChatUserBubbleColor}
                      spellCheck={false}
                      value={formState.chatUserBubbleColor}
                    />
                  </div>
                  <div className="flex min-h-[52px] items-center justify-end rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3">
                    <span
                      className="max-w-full rounded-[18px] px-3.5 py-2 text-sm leading-5"
                      data-testid="branding-chat-user-bubble-preview"
                      style={{
                        backgroundColor: normalizeChatUserBubbleColor(
                          formState.chatUserBubbleColor,
                        ),
                        color: resolveChatUserBubbleTextColor(formState.chatUserBubbleColor),
                      }}
                    >
                      {t('branding.preview.chatUserBubble')}
                    </span>
                  </div>
                </div>
              </FieldBlock>
            </div>
            <div className="flex justify-end">
              <Button
                disabled={!isFormDirty}
                isLoading={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {t('branding.saveSettings')}
              </Button>
            </div>
          </SettingsCard>

          <SettingsCard
            description={t('branding.assetsDescription')}
            title={t('branding.assetsTitle')}
          >
            <div className="grid gap-7">
              <BrandAssetRow label={t('branding.logoTitle')}>
                <BrandAssetControl
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  assetType="logo"
                  canReset={Boolean(formState.logo)}
                  onUpload={(file) => uploadMutation.mutate({ assetType: 'logo', file })}
                  onReset={() => setResetAssetType('logo')}
                  hint={t('branding.logoHint')}
                  previewAlt={t('branding.logoPreviewAlt')}
                  previewSrc={formState.logo || defaultLogoUrl}
                  resetLabel={t('branding.resetAsset')}
                  uploadDisabled={assetControlsLocked}
                  resetPending={deleteMutation.isPending && deleteMutation.variables === 'logo'}
                  uploadLabel={t('branding.uploadAsset')}
                  uploadingLabel={t('branding.uploadingAsset')}
                  uploadPending={
                    uploadMutation.isPending && uploadMutation.variables.assetType === 'logo'
                  }
                />
              </BrandAssetRow>
              <BrandAssetRow label={t('branding.faviconTitle')}>
                <BrandAssetControl
                  accept="image/png,image/jpeg,image/webp,image/gif,image/x-icon,.ico"
                  assetType="favicon"
                  canReset={Boolean(formState.favicon)}
                  onUpload={(file) => uploadMutation.mutate({ assetType: 'favicon', file })}
                  onReset={() => setResetAssetType('favicon')}
                  hint={t('branding.faviconHint')}
                  previewAlt={t('branding.faviconPreviewAlt')}
                  previewSrc={formState.favicon || defaultLogoUrl}
                  resetLabel={t('branding.resetAsset')}
                  uploadDisabled={assetControlsLocked}
                  resetPending={deleteMutation.isPending && deleteMutation.variables === 'favicon'}
                  uploadLabel={t('branding.uploadAsset')}
                  uploadingLabel={t('branding.uploadingAsset')}
                  uploadPending={
                    uploadMutation.isPending && uploadMutation.variables.assetType === 'favicon'
                  }
                />
              </BrandAssetRow>
            </div>
          </SettingsCard>
          <WorkspaceBackgroundEditor
            key={workspaceBackgroundKey(settings)}
            settings={settings}
            userBubbleColor={normalizeChatUserBubbleColor(formState.chatUserBubbleColor)}
          />
        </PageContentBoundary>
      </div>
      <ResetBrandAssetDialog
        assetLabel={resetAssetLabel}
        isSubmitting={deleteMutation.isPending}
        onClose={closeResetDialog}
        onConfirm={confirmResetAsset}
        open={Boolean(resetAssetType)}
      />
    </>
  )
}

function SettingsCard({
  children,
  description,
  title,
}: {
  children: ReactNode
  description?: string
  title: string
}) {
  return (
    <section className="grid gap-5 rounded-lg border border-[var(--color-border)] bg-white p-5 shadow-[0_1px_2px_rgba(26,28,25,0.04)]">
      <div className="grid gap-1">
        <h2 className="text-base font-bold text-[var(--color-text)]">{title}</h2>
        {description ? (
          <p className="text-sm leading-6 text-[var(--color-text-muted)]">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  )
}

function FieldBlock({
  children,
  className,
  error,
  hint,
  htmlFor,
  label,
  required = false,
}: {
  children: ReactNode
  className?: string
  error?: string
  hint?: string
  htmlFor: string
  label: string
  required?: boolean
}) {
  const labelClassName = 'text-sm font-medium leading-5 text-[var(--color-text)]'
  const errorId = `${htmlFor}-error`

  return (
    <div className={`grid min-w-0 gap-2 ${className ?? ''}`}>
      <label className={labelClassName} htmlFor={htmlFor}>
        {label}
        {required ? <span className="ml-1 text-[var(--color-danger)]">*</span> : null}
      </label>
      {children}
      {hint && !error ? (
        <p className="text-xs text-[var(--color-text-muted)]" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="text-xs text-[var(--color-danger)]" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  )
}

function BrandAssetRow({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="grid gap-3">
      <h3 className="text-sm font-semibold leading-5 text-[var(--color-text)]">{label}</h3>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

function BrandAssetControl({
  accept,
  assetType,
  canReset,
  hint,
  onReset,
  onUpload,
  previewAlt,
  previewSrc,
  resetLabel,
  resetPending,
  uploadDisabled,
  uploadLabel,
  uploadingLabel,
  uploadPending,
}: {
  accept: string
  assetType: BrandAssetType
  canReset: boolean
  hint: string
  onReset: () => void
  onUpload: (file: File) => void
  previewAlt: string
  previewSrc: string
  resetLabel: string
  resetPending: boolean
  uploadDisabled: boolean
  uploadLabel: string
  uploadingLabel: string
  uploadPending: boolean
}) {
  const { t } = useTranslation('adminSettings')
  const inputRef = useRef<HTMLInputElement | null>(null)
  const inputId = `branding-${assetType}-upload`

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }
    onUpload(file)
  }

  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)] items-start gap-x-4 gap-y-3">
      <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-[#d5d8e1] bg-white">
        {previewSrc ? (
          <img alt={previewAlt} className="h-full w-full object-contain p-2" src={previewSrc} />
        ) : (
          <span className="px-2 text-center text-xs text-[var(--color-text-muted)]">
            {t('branding.emptyAsset')}
          </span>
        )}
      </div>
      <input
        accept={accept}
        aria-hidden="true"
        className="hidden"
        id={inputId}
        onChange={handleFileChange}
        ref={inputRef}
        tabIndex={-1}
        type="file"
      />
      <div className="grid min-w-0 gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            aria-busy={uploadPending}
            className="relative h-10 w-[88px] overflow-hidden rounded-full px-0"
            disabled={uploadDisabled}
            onClick={() => inputRef.current?.click()}
            variant="secondary"
          >
            {uploadPending ? (
              <span
                aria-hidden
                className="brand-upload-progress absolute inset-y-0 left-0 w-1/2 bg-[rgba(25,91,255,0.18)]"
              />
            ) : null}
            <span className="relative z-10">{uploadPending ? uploadingLabel : uploadLabel}</span>
          </Button>
          <Button
            className="h-10 min-w-20 rounded-full px-4"
            disabled={!canReset || resetPending || uploadDisabled}
            isLoading={resetPending}
            onClick={onReset}
            variant="dangerSoft"
          >
            {resetLabel}
          </Button>
        </div>
        <p className="text-xs leading-5 text-[var(--color-text-muted)]">{hint}</p>
      </div>
    </div>
  )
}

function ResetBrandAssetDialog({
  assetLabel,
  isSubmitting,
  onClose,
  onConfirm,
  open,
}: {
  assetLabel: string
  isSubmitting: boolean
  onClose: () => void
  onConfirm: () => void
  open: boolean
}) {
  const { t } = useTranslation('adminSettings')

  return (
    <Modal
      closeLabel={t('branding.resetDialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('branding.resetDialog.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onConfirm} size="sm" variant="danger">
            {t('branding.resetDialog.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={t('branding.resetDialog.title')}
    >
      <p className="text-sm leading-6 text-[var(--color-text)]">
        {t('branding.resetDialog.message', { asset: assetLabel })}
      </p>
    </Modal>
  )
}
